import React, { useMemo } from 'react'
import { ClientSideTable } from '../Table'

import ProductCell from './Components/ProductCell'
import { useLang } from '_metronic/i18n/Metronici18n'
import moment from 'moment'
import formatNumberCommas from 'Utils/formatNumberCommas'

interface Props {
	code: string
	uuId: string
	status: string
	statusTitleEn: string
	statusTitleTh: string
	createdAt: string
	remainingRequest: number

	viewDetail?: () => void

	oemRequest: {
		expiredAt: string
		items: {
			product: {
				nameEn: string
				nameTh: string
				shortDescription: string
				featurePicturePath: string
				productLine?: {
					nameEn: string
					nameTh: string
					parent?: {
						nameEn: string
						nameTh: string
					}
				}
			}
			quantities: {
				quantity: number
				targetPrice: number
			}[]
		}[]
	}

	quotation?: {
		isNegotiate: boolean
	}
}

const RequestCardOverview: React.FC<Props> = ({
	code,
	status,
	statusTitleEn,
	statusTitleTh,
	createdAt,

	remainingRequest,
	viewDetail,

	oemRequest,
	quotation,
}) => {
	const currentLanguage = useLang()

	const remainingHours = moment(oemRequest?.expiredAt).diff(moment(), 'hours')
	const isDone = remainingRequest === 0 && remainingHours === 0

	const buttonClass = useMemo(() => {
		if (quotation?.isNegotiate) {
			return 'btn btn-sm btn-kumopack-warning-600 text-kumopack-base-white fw-bold'
		}

		if (remainingHours < 0 && status === '10') {
			return 'btn btn-sm btn-kumopack-error-50 text-kumopack-error-700 fw-bold'
		}

		if (status === '30') {
			return 'btn btn-sm btn-kumopack-error-50 text-kumopack-error-700 fw-bold'
		}

		if (status === '60') {
			return 'btn btn-sm btn-kumopack-success-600 text-kumopack-base-white fw-bold'
		}

		return 'btn-sm btn btn-kumopack-secondary-600 text-kumopack-base-white fw-bold'
	}, [status, remainingHours, quotation?.isNegotiate])

	const statusText = useMemo(() => {
		if (remainingHours < 0 && status === '10') {
			return currentLanguage === 'en' ? 'Expired' : 'หมดอายุ'
		} else if (quotation?.isNegotiate) {
			return currentLanguage === 'en' ? 'Price negotiate' : 'ต่อรองราคา'
		} else {
			return currentLanguage === 'en' ? statusTitleEn : statusTitleTh
		}
	}, [
		remainingHours,
		currentLanguage,
		statusTitleEn,
		statusTitleTh,
		status,
		quotation?.isNegotiate,
	])

	const processedData = (oemRequest?.items ?? []).reduce((acc: any[], item) => {
		// item.quantities.forEach(quantity => {
		// 	acc.push({
		// 		product: item.product,
		// 		quantities: {
		// 			quantity: quantity.quantity,
		// 			unit: currentLanguage === 'en' ? 'pcs' : 'ชิ้น',
		// 		},
		// 	})
		// })

		acc.push(item)

		return acc
	}, [])

	const columns: any[] = [
		{
			Header: currentLanguage === 'en' ? 'Product' : 'สินค้าที่สั่งผลิต',
			accessor: 'product',
			width: '75%',
			minWidth: '75%',
			Cell: ({ value }: any) => <ProductCell {...value} />,
		},
		{
			Header: () => (
				<div className="text-end">{currentLanguage === 'en' ? 'Quantity' : 'จำนวนที่สั่งผลิต'}</div>
			),
			accessor: 'quantities',
			width: '25%',
			minWidth: '25%',
			Cell: ({ value }: any) => (
				<React.Fragment>
					{value.map((item: any, index: number) => (
						<div key={index} className="text-end">
							<div className={'d-inline-block text-kumopack-grey-900 fw-bold fs-4'}>
								x {formatNumberCommas(item.quantity)}
							</div>
							<div
								className="ms-2 d-inline-block text-kumopack-grey-600 fs-6"
								style={{ whiteSpace: 'nowrap' }}>
								{currentLanguage === 'en' ? 'pcs' : 'ชิ้น'}
							</div>
						</div>
					))}
				</React.Fragment>
			),
		},
	]

	return (
		<div className="card border border-kumopack-grey-200">
			{/* begin:: Header */}
			<div className="card-header">
				<div className="d-flex flex-column flex-xl-row align-items-start align-items-xl-center justify-content-between w-100 my-5">
					{/* begin:: Title */}
					<div>
						<div className="d-flex flex-row align-items-center" style={{ columnGap: '12px' }}>
							<h4
								className="text-kumopack-primary-600 fw-bold mb-0 cursor-pointer hover-underline"
								onClick={viewDetail}>
								{currentLanguage === 'en' ? 'Code' : 'หมายเลขคำสั่งซื้อ'}:{' '}
								{String(code).length === 0 || !code ? '-' : code}
							</h4>
							{remainingHours >= 0 && status === '10' && (
								<div
									className={`btn btn-sm px-2 py-1 text-white border-radius-16px ${buttonClass}`}>
									{isDone ? 'Done' : 'New Orders'}
								</div>
							)}
						</div>
					</div>
					{/* end:: Title */}

					{/* begin:: Status */}
					<div
						className="d-flex flex-row align-items-center mt-5 mt-xl-0"
						style={{ columnGap: '12px' }}>
						{status === '10' && remainingHours >= 0 && (
							<div className="text-kumopack-grey-600 fw-bold" style={{ whiteSpace: 'nowrap' }}>
								เหลือเวลา {remainingHours} ชม.
							</div>
						)}

						<button className={buttonClass} style={{ whiteSpace: 'nowrap' }} onClick={viewDetail}>
							{statusText}
						</button>

						{/* {isDone ? (
							<button
								className="btn btn-sm btn-kumopack-secondary-50 text-kumopack-secondary-700 fw-bold"
								style={{ whiteSpace: 'nowrap' }}>
								ผู้ผลิต {supplier}
							</button>
						) : (
							<div className="text-kumopack-grey-600 fw-bold">เหลือเวลา {remainingHours} ชม.</div>
						)}

						<button
							className="btn btn-sm btn-kumopack-secondary-600 text-kumopack-base-white fw-bold"
							style={{ whiteSpace: 'nowrap' }}>
							{isDone
								? 'ผู้ผลิตส่งสินค้าแล้ว'
								: `กำลังรอใบเสนอราคา ${remainingRequest}/${totalRequest}`}
						</button> */}

						{/* <button className="btn btn-sm white-button text-kumopack-grey-700 fw-bold">
							ให้คะแนนสินค้า
						</button>

						<button
							className="btn btn-sm btn-kumopack-secondary-600 text-kumopack-base-white fw-bold"
							style={{ whiteSpace: 'nowrap' }}>
							สั่งซื้ออีกครั้ง
						</button> */}
						{/* 
						<button className="d-none d-xl-block btn btn-icon btn-custom btn-active-light rounded-circle">
							<img
								src="/media/icons/kumopack/dots-vertical.png"
								alt="Dot Vertical Icon"
								width={20}
								height={20}
								style={{ userSelect: 'none', pointerEvents: 'none' }}
							/>
						</button> */}
					</div>
					{/* end:: Status */}
				</div>
			</div>
			{/* end:: Header */}

			<hr
				style={{
					background: 'transparent',
					borderBottom: '1px solid #CCC',
					borderTop: 0,
					borderLeft: 0,
					borderRight: 0,
					margin: 0,
				}}
			/>

			{/* begin:: Body */}
			<div className="card-body p-0">
				<ClientSideTable isGrey data={processedData} columns={columns} items_per_page={9999} />

				<hr
					style={{
						background: 'transparent',
						borderBottom: '1px solid #CCC',
						borderTop: 0,
						borderLeft: 0,
						borderRight: 0,
						margin: 0,
					}}
				/>

				<div className="d-flex flex-row justify-content-between p-5">
					<div className="text-kumopack-grey-700">
						{currentLanguage === 'en' ? 'Created At' : 'สร้างเมื่อวันที่'}{' '}
						{moment(createdAt).format('DD-MM-YYYY')}
					</div>
					<button
						className="btn btn-kumopack-primary-600 text-kumopack-base-white p-2 fs-7"
						onClick={viewDetail}>
						{currentLanguage === 'en' ? 'View Detail' : 'รายละเอียดเพิ่มเติม'}
					</button>
				</div>
			</div>
			{/* end:: Body */}
		</div>
	)
}

export default RequestCardOverview
